/**
 * Capacity honesty banner.
 *
 * A capacity surface reading 0h / 0% is ambiguous: it can mean "measured, and
 * nobody is loaded" or "we could not model any of this work". Rendering the
 * second as the first is the failure this component exists to prevent — the
 * same class as the Risk page's migration-unavailable state.
 *
 * Renders nothing when the data is genuinely complete, so a healthy workspace
 * pays no visual cost.
 */

export interface CapacityDataQualityReasons {
  noAssignee: number;
  noDates: number;
  capacityDisabled: number;
}

export interface CapacityDataQualityBannerProps {
  complete: boolean;
  unmodeledTaskCount: number;
  reasons: CapacityDataQualityReasons;
  /** Workspace scope only — evidenced hours that went unplaced. */
  unmodeledHours?: number;
  /**
   * Org scope only. The org rollup reads stored capacity rows and synthesises
   * no defaults, so zero rows means the result is empty for want of input.
   */
  capacityCalendarEntries?: number;
  scope: 'workspace' | 'organization';
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export default function CapacityDataQualityBanner({
  complete,
  unmodeledTaskCount,
  reasons,
  unmodeledHours,
  capacityCalendarEntries,
  scope,
}: CapacityDataQualityBannerProps) {
  const noCalendar = capacityCalendarEntries === 0;

  // Nothing to disclose.
  if (complete && !noCalendar) return null;

  const lines: string[] = [];

  if (noCalendar) {
    lines.push(
      'No capacity calendar entries are recorded, so utilization cannot be computed. ' +
        'The figures below are empty for lack of input — not because utilization is zero.',
    );
  }
  if (reasons.noAssignee > 0) {
    lines.push(
      `${plural(reasons.noAssignee, 'work item is', 'work items are')} missing an assignee — ` +
        'demand is per-person, so these cannot be placed on anyone’s calendar.',
    );
  }
  if (reasons.noDates > 0) {
    lines.push(
      `${plural(reasons.noDates, 'work item is', 'work items are')} missing a start or end date — ` +
        'these cannot be spread across a date range.',
    );
  }
  if (reasons.capacityDisabled > 0) {
    lines.push(
      `${plural(reasons.capacityDisabled, 'project has', 'projects have')} capacity tracking turned off — ` +
        'their work is excluded entirely.',
    );
  }

  const headline =
    unmodeledTaskCount > 0
      ? `Capacity data incomplete — ${plural(unmodeledTaskCount, 'work item', 'work items')} could not be modeled`
      : 'Capacity data incomplete';

  return (
    <div
      data-testid="capacity-data-incomplete"
      role="status"
      className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3"
    >
      <p className="text-sm font-semibold text-amber-900">{headline}</p>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-800">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {/*
        Only shown when we have evidenced hours. An unestimated task
        contributes 0 to this total, so it is a floor, never a true figure —
        say so rather than let it read as a measurement.
      */}
      {scope === 'workspace' && unmodeledHours !== undefined && unmodeledHours > 0 && (
        <p className="mt-2 text-xs text-amber-700">
          At least {unmodeledHours}h of estimated effort is excluded from the totals below.
          Unestimated work adds an unknown amount on top.
        </p>
      )}
      <p className="mt-2 text-xs text-amber-700">
        {scope === 'organization'
          ? 'Organization capacity figures below reflect only the work that could be modeled.'
          : 'Utilization and overallocation below reflect only the work that could be modeled.'}
      </p>
    </div>
  );
}
